import React, { useEffect, useRef, useState } from 'react';
import Button from 'shared/components/UC/Button';
import styles from './GroupedMultiSelect.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

export interface OptionGroup {
  label?: string;
  options: SelectOption[];
}

interface GroupedMultiSelectProps {
  groups: OptionGroup[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const GroupedMultiSelect: React.FC<GroupedMultiSelectProps> = ({
  groups,
  selected,
  onChange,
  placeholder = 'Seleccionar...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync draft from applied selection each time the dropdown opens.
  useEffect(() => {
    if (isOpen) setDraft(selected);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside closes without applying (draft is discarded).
  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [isOpen]);

  const toggleDraft = (value: string) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const apply = () => {
    onChange(draft);
    setIsOpen(false);
  };

  const clear = () => {
    setDraft([]);
    onChange([]);
    setIsOpen(false);
  };

  const allOptions = groups.flatMap((g) => g.options);

  // Trigger shows the APPLIED selection, not the draft.
  const triggerLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
      ? (allOptions.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} tipos seleccionados`;

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ''} ${selected.length > 0 ? styles.triggerActive : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        <i className="uc-icon icon-size--sm">{isOpen ? 'expand_less' : 'expand_more'}</i>
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="listbox" aria-multiselectable="true">
          {groups.map((group, i) => (
            <div key={group.label ?? i} className={styles.group}>
              {group.label && <div className={styles.groupHeader}>{group.label}</div>}
              {group.options.map((opt) => {
                const checked = draft.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`${styles.option} ${checked ? styles.optionChecked : ''}`}
                  >
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={checked}
                      onChange={() => toggleDraft(opt.value)}
                    />
                    <span className={styles.optionLabel}>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          ))}

          <div className={styles.footer}>
            {draft.length > 0 && (
              <button type="button" className={styles.clearBtn} onClick={clear}>
                Limpiar
              </button>
            )}
            <Button type="button" variant="primary" size="sm" onClick={apply}>
              Aplicar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedMultiSelect;
